import { useState, useEffect, useCallback } from "react";
import { Mail, Send, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import adminApi from "@/lib/adminApi";

export default function AdminEmails() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, logRes] = await Promise.all([
        adminApi.get("/api/admin/emails/status"),
        adminApi.get("/api/admin/emails/log?limit=20"),
      ]);
      setStatus(statusRes.data);
      setLog(logRes.data.entries || []);
    } catch (err) { console.error("[AdminEmails] fetch failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendTest = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await adminApi.post("/api/admin/emails/send-test");
      setSendResult(res.data);
      fetchData();
    } catch (err) {
      setSendResult({ success: false, message: err.response?.data?.detail || err.message });
    }
    setSending(false);
  };

  if (loading) return <div className="border border-white/[0.08] bg-[#060606] p-8 text-center text-xs text-white/30">Loading email status...</div>;

  return (
    <div className="space-y-6" data-testid="admin-emails">
      {/* Config Status */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Mail size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Email Integration</h3>
          <span className={`ml-auto text-xs font-semibold ${status?.configured ? "text-green-400" : "text-red-400"}`}>
            {status?.configured ? "Configured" : "Not configured"}
          </span>
        </div>
        <div className="p-5">
          <div className="space-y-2">
            <div className="flex justify-between py-2 border-b border-white/[0.04]">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Provider</span>
              <span className="text-xs text-white/70">{status?.provider || "Resend"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/[0.04]">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">API Key</span>
              <span className={`text-xs ${status?.api_key_set ? "text-green-400" : "text-red-400"}`}>{status?.api_key_set ? "Set" : "Missing"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Notification Email</span>
              <span className="text-xs text-white/70">{status?.notification_email}</span>
            </div>
          </div>

          {status?.configured && (
            <div className="mt-4">
              <button onClick={sendTest} disabled={sending} className="flex items-center gap-2 text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="email-send-test">
                <Send size={12} className={sending ? "animate-pulse" : ""} />
                {sending ? "Sending..." : "Send Test Email"}
              </button>
              {sendResult && (
                <div className={`mt-2 p-2 rounded text-xs ${sendResult.success ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                  {sendResult.success ? <CheckCircle size={12} className="inline mr-1" /> : <XCircle size={12} className="inline mr-1" />}
                  {sendResult.message}
                </div>
              )}
            </div>
          )}

          {!status?.configured && (
            <div className="mt-4 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center gap-2 text-xs text-yellow-400"><AlertTriangle size={12} /><span>Email not configured</span></div>
              <p className="text-[10px] text-yellow-400/60 mt-1">Set RESEND_API_KEY and QUOTE_NOTIFICATION_EMAIL in backend .env</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Log */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Clock size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Email Log</h3>
        </div>
        <div className="p-5">
          {log.length === 0 ? (
            <p className="text-xs text-white/30 text-center py-4">No emails sent yet.</p>
          ) : (
            <div className="space-y-2">
              {log.map((entry, i) => (
                <div key={`email-${i}`} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0">
                  {entry.status === "sent" ? <CheckCircle size={12} className="text-green-400 shrink-0" /> : <XCircle size={12} className="text-red-400 shrink-0" />}
                  <span className="text-[11px] text-white/60 flex-1 truncate">{entry.subject || entry.type} &rarr; {entry.to}</span>
                  <span className="text-[10px] text-white/25">{entry.sent_at ? new Date(entry.sent_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
